import {
  oneWords,
  ROOT,
  textWords,
  type WizardFieldErrors,
  type WizardRefusalWording,
} from "@/base";
import type { FieldOption, ProductManifest } from "@/lib/brokerProducts";
import type { PriorCarrier } from "@/lib/brokerPriorCarriers";
import { shiftIsoMonths, shiftIsoYears } from "@/lib/dates";
import type { SubmissionDetail } from "@/lib/submissions/brokerSubmissions";
import type { LossRuns } from "@/lib/submissions/lossRuns";
import type {
  PriorCoverageInput,
  PriorCoverageRow,
} from "@/lib/submissions/priorCoverage";
import {
  answersComplete,
  answersFrom,
  HISTORY,
  historyFields,
  isAsked,
  shownAnswers,
  validateAnswers,
  type WcAnswer,
  type WcAnswers,
} from "@/lib/submissions/wc/questions";
import { newRowKey } from "@/lib/submissions/wc/rowKey";

// The WC wizard's Business › Characteristics & History (BRK-S389), as pure
// rules. Tyler's doc and Nav, 10/21–22; PMS-S809 asks the questions, PMS-S810
// keeps the rows:
// - Current coverage is a plain Yes/No. Yes asks the carrier; No asks why,
//   and the business's age decides the reasons: under a month old, "New in
//   business" is chosen for the broker; over six months, it isn't offered.
// - A business with coverage now, or whose coverage lapsed, says how many
//   years it had, then one row a year, newest first, each dated from the
//   desired effective date, each with its claims, starting at 0. Whether it
//   had claims in the past 3 years is worked out from Years 1 to 3, never
//   asked, and shown beside their sum (BRK-S505, PMS-S909; Nav, 2026-10-29).
// - Years with the current carrier isn't asked: PMS works it out from the
//   rows.
// - A new business's loss runs are recorded as "A new business", once.
// The questions, their wording and their order are the manifest's; the rules
// here read PMS's keys for them.

export const CURRENT_CARRIER = "currentCarrierId";
export const NO_COVERAGE_REASON = "noCoverageReason";
export const PRIOR_COVERAGE_YEARS = "priorCoverageYears";
/** How many years of claims are counted: Years 1 to 3. */
export const CLAIM_YEARS = 3;
/** The claims in the past 3 years as the page shows them, worked out from
 *  the cards, never asked (BRK-S505). */
export const CLAIMS_QUESTION =
  "Has the business had any workers' comp claims in the past 3 years?";
export const CLAIMS_TOTAL = "Claims in the past 3 years";
/** The reason a business under a month old has, chosen for the broker. */
export const NEW_IN_BUSINESS = "new_business";

/** Every reason for no coverage the age rule knows: the runtime copy the
 *  contract test holds to the pin. */
export const NO_COVERAGE_REASONS = [
  NEW_IN_BUSINESS,
  "no_wc_coverage",
  "coverage_lapsed",
] as const;

/** How many rows each answer to the years question asks for: one a year,
 *  four at most (PMS-S810). */
export const PRIOR_COVERAGE_ROWS: Readonly<Record<string, number>> = {
  "1_or_less": 1,
  "2": 2,
  "3": 3,
  "4_or_more": 4,
};

/** The page's own words for a question left unanswered, by key. */
const HISTORY_COPY: Readonly<Record<string, string>> = {
  [CURRENT_CARRIER]: "Choose the current carrier.",
  [NO_COVERAGE_REASON]: "Choose why the business has no coverage.",
  [PRIOR_COVERAGE_YEARS]: "Choose how many years of prior coverage.",
};

/** How old the business is: under a month, up to six months, or older. */
export type BusinessAge = "new" | "young" | "established";

/** The business's age from its start date and today, on the calendar; null
 *  without a start date. */
export function businessAge(
  startDate: string | null | undefined,
  today: string,
): BusinessAge | null {
  if (!startDate) return null;
  if (startDate > shiftIsoMonths(today, -1)) return "new";
  if (startDate < shiftIsoMonths(today, -6)) return "established";
  return "young";
}

/** The reasons offered at the business's age: all but "New in business"
 *  once it is over six months old. */
export function noCoverageOptions(
  options: readonly FieldOption[],
  age: BusinessAge | null,
): FieldOption[] {
  return age === "established"
    ? options.filter((o) => o.value !== NEW_IN_BUSINESS)
    : [...options];
}

/** The reason chosen for the broker, which they can change: "New in
 *  business" under a month old. */
export function noCoverageDefault(age: BusinessAge | null): string | undefined {
  return age === "new" ? NEW_IN_BUSINESS : undefined;
}

/** How many rows the years answer asks for; undefined while unanswered. */
export function coverageYearCount(years: WcAnswer): number | undefined {
  return typeof years === "string" ? PRIOR_COVERAGE_ROWS[years] : undefined;
}

/** One year of prior coverage as the page holds it. */
export interface WcCoverageYear {
  key: string;
  /** PMS's row, once saved. */
  id?: string;
  /** "" until chosen. */
  carrierId: string;
  /** What was typed with the Other carrier. */
  carrierName: string;
  /** ISO YYYY-MM-DD, "" until known. */
  effectiveDate: string;
  expirationDate: string;
  premium: number | undefined;
  /** The year's claims (BRK-S505, PMS-S909): not required, 0 when new;
   *  blank counts as 0. */
  claimCount: number | undefined;
}

/** A count of years as the page says it: "1 year", "2 years". */
export function yearsText(n: number): string {
  return `${n} ${n === 1 ? "year" : "years"}`;
}

/** A card's title: the newest year is Year 1. */
export function coverageYearTitle(index: number): string {
  return index === 0 ? "Year 1 (most recent)" : `Year ${index + 1}`;
}

/** Year `year` (1 is the newest), dated from the desired effective date: it
 *  starts that many years before it and runs a year. Undated without one. */
export function newCoverageYear(
  year: number,
  effectiveDate: string,
  carrierId = "",
): WcCoverageYear {
  return {
    key: newRowKey(),
    carrierId,
    carrierName: "",
    effectiveDate: effectiveDate ? shiftIsoYears(effectiveDate, -year) : "",
    expirationDate: effectiveDate
      ? shiftIsoYears(effectiveDate, -(year - 1))
      : "",
    premium: undefined,
    claimCount: 0,
  };
}

/** The rows for `count` years: those already there, newest first, then new
 *  ones dated from the desired effective date. Year 1, when new, is the
 *  current carrier's. */
export function resizeCoverageYears(
  rows: readonly WcCoverageYear[],
  count: number,
  from: { effectiveDate: string; currentCarrierId?: string },
): WcCoverageYear[] {
  const kept = rows.slice(0, count);
  for (let i = kept.length; i < count; i++) {
    kept.push(
      newCoverageYear(
        i + 1,
        from.effectiveDate,
        i === 0 ? (from.currentCarrierId ?? "") : "",
      ),
    );
  }
  return kept;
}

/** PMS's rows as the cards show them, newest first (PMS sends the oldest
 *  first). */
export function coverageYearsFrom(
  rows: readonly PriorCoverageRow[],
): WcCoverageYear[] {
  return [...rows]
    .sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate))
    .map((r) => ({
      key: r.id,
      id: r.id,
      carrierId: r.priorCarrierId ?? "",
      carrierName: r.carrierName ?? "",
      effectiveDate: r.effectiveDate,
      expirationDate: r.expirationDate,
      premium: r.totalPremium ?? undefined,
      // A row saved before PMS-S909 holds none: 0, as a new card starts.
      claimCount: r.claimCount ?? 0,
    }));
}

const isOther = (carriers: readonly PriorCarrier[], id: string) =>
  carriers.some((c) => c.id === id && c.isOther);

/** The rows as PUT …/prior-coverage takes them, in the cards' order: ISO
 *  dates, a plain number, and the typed name only with Other (D-10). */
export function coverageYearsToWire(
  rows: readonly WcCoverageYear[],
  carriers: readonly PriorCarrier[],
): PriorCoverageInput[] {
  return rows.map((r) => ({
    ...(r.id ? { id: r.id } : {}),
    priorCarrierId: r.carrierId,
    ...(isOther(carriers, r.carrierId)
      ? { carrierName: r.carrierName.trim() }
      : {}),
    effectiveDate: r.effectiveDate,
    expirationDate: r.expirationDate,
    totalPremium: r.premium ?? 0,
    claimCount: r.claimCount ?? 0,
  }));
}

/** The claims in the past 3 years: the sum of Years 1 to 3's (fewer cards,
 *  fewer years; a blank one counts as 0), as PMS works out
 *  `wcClaimsTotal3Years` (PMS-S909). Undefined without a card. */
export function claimsInPast3Years(
  years: readonly WcCoverageYear[],
): number | undefined {
  const counted = years.slice(0, CLAIM_YEARS);
  if (counted.length === 0) return undefined;
  return counted.reduce((sum, y) => sum + (y.claimCount ?? 0), 0);
}

// A year's sentences, said alike by the checks and for PMS's (BRK-S339).
const CARRIER = "Choose the carrier.";
const CARRIER_NAME = "Enter the carrier’s name.";
const EFFECTIVE = "Enter the effective date.";
const EXPIRATION = "Enter the expiration date.";
const PREMIUM = "Enter the premium.";
const CLAIMS = "Enter the number of claims.";

/** What PMS's checks of the prior coverage write refuse, in the step's words
 *  (BRK-S339), on the year's card. */
export const HISTORY_REFUSALS: WizardRefusalWording = {
  "priorCoverage.*.priorCarrierId": oneWords(CARRIER),
  "priorCoverage.*.carrierName": textWords(CARRIER_NAME, "carrier’s name"),
  "priorCoverage.*.effectiveDate": oneWords(EFFECTIVE),
  "priorCoverage.*.expirationDate": oneWords(EXPIRATION),
  "priorCoverage.*.totalPremium": {
    ...oneWords("Enter the premium as $0 or more."),
    required: PREMIUM,
  },
  "priorCoverage.*.claimCount": {
    ...oneWords("Enter the number of claims as a whole number, 0 or more."),
    required: CLAIMS,
  },
};

/** Each row's checks, keyed as PMS keys a refused row
 *  (`priorCoverage.N.<field>`, N the card's index), so its errors and PMS's
 *  land on the same card. */
export function validateCoverageYears(
  rows: readonly WcCoverageYear[],
  carriers: readonly PriorCarrier[],
): WizardFieldErrors {
  const errors: Record<string, string> = {};
  rows.forEach((r, i) => {
    const at = (field: string) => `priorCoverage.${i}.${field}`;
    if (!r.carrierId) errors[at("priorCarrierId")] = CARRIER;
    else if (isOther(carriers, r.carrierId) && !r.carrierName.trim())
      errors[at("carrierName")] = CARRIER_NAME;
    if (!r.effectiveDate) errors[at("effectiveDate")] = EFFECTIVE;
    if (!r.expirationDate) errors[at("expirationDate")] = EXPIRATION;
    else if (r.effectiveDate && r.expirationDate <= r.effectiveDate)
      errors[at("expirationDate")] =
        "The expiration date must be after the effective date.";
    if (r.premium === undefined) errors[at("totalPremium")] = PREMIUM;
    // Not required: a blank one is sent as 0.
    if (
      r.claimCount !== undefined &&
      (!Number.isInteger(r.claimCount) || r.claimCount < 0)
    )
      errors[at("claimCount")] =
        "Enter the number of claims as a whole number, 0 or more.";
  });
  return errors;
}

/** How many rows the page asks for now: none while the years question isn't
 *  asked or answered. */
export function askedCoverageYears(
  manifest: ProductManifest | null,
  answers: WcAnswers,
): number | undefined {
  const years = historyFields(manifest).find(
    (f) => f.key === PRIOR_COVERAGE_YEARS,
  );
  const shown = shownAnswers(manifest?.fields ?? [], answers);
  return years && isAsked(years, shown)
    ? coverageYearCount(answers[PRIOR_COVERAGE_YEARS])
    : undefined;
}

/** The page's checks: its questions in its own words, a reason the
 *  business's age offers, and a row for each year asked. The rows wait for
 *  the carriers, which say which one is Other. */
export function validateHistory(
  manifest: ProductManifest | null,
  answers: WcAnswers,
  rows: readonly WcCoverageYear[],
  context: {
    carriers: readonly PriorCarrier[] | null;
    age: BusinessAge | null;
  },
): WizardFieldErrors {
  const count = askedCoverageYears(manifest, answers);
  const errors: Record<string, string> = {
    ...validateAnswers(manifest, answers, HISTORY, HISTORY_COPY),
  };
  const reason = historyFields(manifest).find(
    (f) => f.key === NO_COVERAGE_REASON,
  );
  const shown = shownAnswers(manifest?.fields ?? [], answers);
  if (
    reason &&
    isAsked(reason, shown) &&
    answers[NO_COVERAGE_REASON] !== undefined &&
    !noCoverageOptions(reason.options ?? [], context.age).some(
      (o) => o.value === answers[NO_COVERAGE_REASON],
    )
  ) {
    errors[NO_COVERAGE_REASON] = HISTORY_COPY[NO_COVERAGE_REASON];
  }
  if (count === undefined) return errors;
  if (context.carriers === null) {
    return { ...errors, [ROOT]: "The carriers are still loading." };
  }
  if (rows.length < count) {
    errors.priorCoverage = "Enter each year’s dates and premium.";
  }
  return {
    ...errors,
    ...validateCoverageYears(rows.slice(0, count), context.carriers),
  };
}

/** Whether PMS holds the page: every answer it asks, and a row for each year
 *  the years answer asks for. */
export function historyComplete(
  manifest: ProductManifest | null,
  detail: Pick<SubmissionDetail, "data" | "priorCoverage">,
): boolean {
  if (!answersComplete(manifest, detail.data, HISTORY)) return false;
  const count = askedCoverageYears(
    manifest,
    answersFrom(manifest, detail.data),
  );
  return count === undefined || (detail.priorCoverage?.length ?? 0) === count;
}

/** Whether saving records "A new business" for the loss runs: the answer is
 *  New in business, the line asks for loss runs, and none are on file and no
 *  reason is recorded. Never over a reason or a file the broker gave. */
export function recordsNewBusiness(
  lossRuns: LossRuns | undefined,
  reason: WcAnswer,
): boolean {
  return (
    reason === NEW_IN_BUSINESS &&
    lossRuns !== undefined &&
    lossRuns.required &&
    lossRuns.count === 0 &&
    lossRuns.notAvailable === null
  );
}
